import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { ConversationViewType } from '@/api/features/conversations';
import { ComposeRecipient } from '@/api/features/contacts';

// Define the compose state type (UI state only)
interface ComposeState {
  isComposing: boolean;
  selectedRecipients: ComposeRecipient[];
  searchQuery: string;
}

// Define the UI-only state type
interface MessagingUiState {
  // Core UI state
  currentConversationId: string | null;
  previousConversationId: string | null; // Store the conversation ID before entering compose mode
  currentView: ConversationViewType;
  sidebarCollapsed: boolean;
  
  // Compose mode UI state
  compose: ComposeState;
  
  // Additional UI preferences
  showUnreadOnly: boolean;
  selectedTagFilters: string[];
  selectedAssigneeFilters: string[];
}

// Initial state - UI only
const initialState: MessagingUiState = {
  currentConversationId: null,
  previousConversationId: null,
  currentView: 'all',
  sidebarCollapsed: false,
  compose: {
    isComposing: false,
    selectedRecipients: [],
    searchQuery: ''
  },
  showUnreadOnly: false,
  selectedTagFilters: [],
  selectedAssigneeFilters: []
};

// Create the UI-only slice
const messagingUiSlice = createSlice({
  name: 'messagingUi',
  initialState,
  reducers: {
    // Conversation selection (UI state)
    setCurrentConversation(state, action: PayloadAction<string | null>) {
      state.currentConversationId = action.payload;
    },
    
    // View management (UI state)
    setCurrentView(state, action: PayloadAction<ConversationViewType>) {
      state.currentView = action.payload;
    },
    
    // Sidebar UI state
    setSidebarCollapsed(state, action: PayloadAction<boolean>) {
      state.sidebarCollapsed = action.payload;
    },
    
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    
    // Compose mode UI actions
    startComposing(state) {
      // Save the current conversation ID before entering compose mode
      state.previousConversationId = state.currentConversationId;
      state.compose.isComposing = true;
      state.compose.selectedRecipients = [];
      state.compose.searchQuery = '';
      state.currentConversationId = '__COMPOSE__';
    },
    
    stopComposing(state) {
      state.compose.isComposing = false;
      state.compose.selectedRecipients = [];
      state.compose.searchQuery = '';
      if (state.currentConversationId === '__COMPOSE__') {
        // Restore the previous conversation ID when exiting compose mode
        state.currentConversationId = state.previousConversationId;
      }
      // Clear the previous conversation ID after restoring
      state.previousConversationId = null;
    },

    // Atomic transition from compose mode to a specific conversation.
    // Avoids the intermediate state caused by stopComposing() + setCurrentConversation()
    // where currentConversationId is briefly set to previousConversationId.
    finishComposing(state, action: PayloadAction<string>) {
      state.compose.isComposing = false;
      state.compose.selectedRecipients = [];
      state.compose.searchQuery = '';
      state.currentConversationId = action.payload;
      state.previousConversationId = null;
    },
    
    setComposeSearchQuery(state, action: PayloadAction<string>) {
      state.compose.searchQuery = action.payload;
    },
    
    addComposeRecipient(state, action: PayloadAction<ComposeRecipient>) {
      const recipient = action.payload;
      // Only allow one recipient - replace any existing recipients
      state.compose.selectedRecipients = [recipient];
      // Clear search query when recipient is selected
      state.compose.searchQuery = '';
    },
    
    removeComposeRecipient(state, action: PayloadAction<string>) {
      const recipientId = action.payload;
      state.compose.selectedRecipients = state.compose.selectedRecipients.filter(
        (r: ComposeRecipient) => r.id !== recipientId
      );
    },
    
    clearComposeRecipients(state) {
      state.compose.selectedRecipients = [];
    },
    
    // Filter UI state
    setShowUnreadOnly(state, action: PayloadAction<boolean>) {
      state.showUnreadOnly = action.payload;
    },
    
    toggleShowUnreadOnly(state) {
      state.showUnreadOnly = !state.showUnreadOnly;
    },
    
    setSelectedTagFilters(state, action: PayloadAction<string[]>) {
      state.selectedTagFilters = action.payload;
    },
    
    addTagFilter(state, action: PayloadAction<string>) {
      if (!state.selectedTagFilters.includes(action.payload)) {
        state.selectedTagFilters.push(action.payload);
      }
    },
    
    removeTagFilter(state, action: PayloadAction<string>) {
      state.selectedTagFilters = state.selectedTagFilters.filter(
        id => id !== action.payload
      );
    },
    
    clearTagFilters(state) {
      state.selectedTagFilters = [];
    },
    
    setSelectedAssigneeFilters(state, action: PayloadAction<string[]>) {
      state.selectedAssigneeFilters = action.payload;
    },
    
    addAssigneeFilter(state, action: PayloadAction<string>) {
      if (!state.selectedAssigneeFilters.includes(action.payload)) {
        state.selectedAssigneeFilters.push(action.payload);
      }
    },
    
    removeAssigneeFilter(state, action: PayloadAction<string>) {
      state.selectedAssigneeFilters = state.selectedAssigneeFilters.filter(
        id => id !== action.payload
      );
    },
    
    clearAssigneeFilters(state) {
      state.selectedAssigneeFilters = [];
    },
    
    // Reset all filters
    clearAllFilters(state) {
      state.showUnreadOnly = false;
      state.selectedTagFilters = [];
      state.selectedAssigneeFilters = [];
    }
  }
});

// Export actions
export const {
  // Conversation selection
  setCurrentConversation,
  
  // View management
  setCurrentView,
  
  // Sidebar
  setSidebarCollapsed,
  toggleSidebar,
  
  // Compose mode
  startComposing,
  stopComposing,
  finishComposing,
  setComposeSearchQuery,
  addComposeRecipient,
  removeComposeRecipient,
  clearComposeRecipients,
  
  // Filters
  setShowUnreadOnly,
  toggleShowUnreadOnly,
  setSelectedTagFilters,
  addTagFilter,
  removeTagFilter,
  clearTagFilters,
  setSelectedAssigneeFilters,
  addAssigneeFilter,
  removeAssigneeFilter,
  clearAssigneeFilters,
  clearAllFilters
} = messagingUiSlice.actions;

// Selectors — typed with slice state to avoid circular dep on RootState
type WithMessagingUi = { messagingUi: MessagingUiState };

export const selectCurrentConversationId = (state: WithMessagingUi) =>
  state.messagingUi.currentConversationId;

export const selectPreviousConversationId = (state: WithMessagingUi) =>
  state.messagingUi.previousConversationId;

export const selectCurrentView = (state: WithMessagingUi) =>
  state.messagingUi.currentView;

export const selectSidebarCollapsed = (state: WithMessagingUi) =>
  state.messagingUi.sidebarCollapsed;

export const selectComposeState = (state: WithMessagingUi) =>
  state.messagingUi.compose;

export const selectIsComposing = (state: WithMessagingUi) =>
  state.messagingUi.compose.isComposing;

export const selectShowUnreadOnly = (state: WithMessagingUi) =>
  state.messagingUi.showUnreadOnly;

export const selectSelectedTagFilters = (state: WithMessagingUi) =>
  state.messagingUi.selectedTagFilters;

export const selectSelectedAssigneeFilters = (state: WithMessagingUi) =>
  state.messagingUi.selectedAssigneeFilters;

// Compound selectors (memoized to prevent recalculation on every call)
export const selectHasActiveFilters = createSelector(
  [(s: WithMessagingUi) => s.messagingUi.showUnreadOnly,
   (s: WithMessagingUi) => s.messagingUi.selectedTagFilters,
   (s: WithMessagingUi) => s.messagingUi.selectedAssigneeFilters],
  (unread, tags, assignees) => unread || tags.length > 0 || assignees.length > 0
);

export const selectIsInComposeMode = createSelector(
  [(s: WithMessagingUi) => s.messagingUi.currentConversationId,
   (s: WithMessagingUi) => s.messagingUi.compose.isComposing],
  (convId, isComposing) => convId === '__COMPOSE__' && isComposing
);

export default messagingUiSlice.reducer; 